import { makeAutoObservable } from 'mobx'
import { ratingApi } from '@/api/rating'
import {
  GetFilterObjects,
  GetRatingReq,
  GetRatingRes,
  IRatingCategory,
  IRatingLevelByCategory,
  IRatingParams,
  IRatingStatistic,
  IRatingStatisticCourse,
  IRatingStatisticGroups,
  RatingCategoryId,
  RatingInterval,
  RatingOrderBy,
  RatingScope,
  StudentRating,
  TBranch,
} from '@/api/rating/types'
import { addAxiosErrorNotification } from '@/components/Notifications'
import { IGetCourseRequest, IRatingLevelSort } from './types'

class RatingStore {
  page = 1
  perPage = 10
  total = 0
  activeCategoryId: number | undefined = 0
  groupId: number | null = null
  courseId: number | null = null
  courses: IRatingStatisticCourse[] = []
  categories: IRatingCategory[] = []
  categoryId: RatingCategoryId | null | undefined = null
  activeStudentRating: IRatingStatistic | undefined = undefined
  studentsRating: GetRatingRes | null = null
  sortBy: IRatingLevelSort = IRatingLevelSort.XP
  orderBy: RatingOrderBy = RatingOrderBy.DESC
  isOpenGroupModal = false
  selectStudentGroup: IRatingStatisticGroups[] = []
  filterLevels: IRatingLevelByCategory[] = []
  level: number | null = null
  startDate: string | null | Date = null
  endDate: string | null | Date = null
  branches: TBranch[] | null = null
  selectedBranch: number | null = null
  method: number | null = null
  filterObjects: GetFilterObjects | null = null
  selectedStatus: string | null = '1'
  currentStudentRating: StudentRating | null = null
  ratingFilter: GetRatingReq = {
    page: 1,
    perPage: 10,
    interval: RatingInterval.Monthly,
    scope: RatingScope.Branch,
  }

  constructor() {
    makeAutoObservable(this)
  }

  setRatingFilter = (params: GetRatingReq) => {
    this.ratingFilter = params
  }

  setSelectedStatus = (params: string | null) => {
    this.selectedStatus = params
  }

  setFilterObjects = (params: GetFilterObjects | null) => {
    this.filterObjects = params
  }

  setMethod = (params: null | number) => {
    this.method = params
  }

  setSelectedBranch = (params: number | null) => {
    this.selectedBranch = params
  }

  setCurrentStudentRating = (student: StudentRating | null) => {
    this.currentStudentRating = student
  }

  getCategories = () =>
    ratingApi
      .getRatingCategories()
      .then((res) => {
        if (res?.success && res?.data) {
          this.setCategories(res.data.categories)
        }
      })
      .catch(addAxiosErrorNotification)

  getFilterObjects = () =>
    ratingApi
      .getFilterObjects()
      .then((res) => {
        if (res.success) {
          this.setFilterObjects(res.data)
        }
      })
      .catch(addAxiosErrorNotification)

  getRating = ({ id, ...rest }: GetRatingReq) =>
    ratingApi
      .getRating(rest)
      .then((res) => {
        if (res?.success && res?.data) {
          const allStudents = res.data?.students

          const currentStudentFromAllStudents = allStudents?.find((student) => student?.id === id)

          this.setCurrentStudentRating(currentStudentFromAllStudents || null)
          this.setStudentsRating(res?.data)
        }
      })
      .catch(addAxiosErrorNotification)

  getBranches = () =>
    ratingApi
      .getBranches()
      .then((res) => {
        if (res.success) {
          this.branches = res?.data?.offices
        }
      })
      .catch(addAxiosErrorNotification)

  getLevelsByCategory = () =>
    ratingApi
      .getLevelByCategory()
      .then((res) => {
        if (res?.success && res?.data) {
          this.setFilterevels(res.data?.levels)
        }
      })
      .catch(addAxiosErrorNotification)

  getCourses = (params?: IGetCourseRequest) =>
    ratingApi
      .getCourses(params)
      .then((res) => {
        if (res?.success && res?.data) {
          this.setCourses(res.data?.courses)
        }
      })
      .catch(addAxiosErrorNotification)

  setActiveCategoryId = (id: number | undefined) => {
    this.activeCategoryId = id
  }

  setCategories = async (categories: IRatingCategory[]) => {
    this.categories = categories
  }

  setStudentsRating = (ratings: GetRatingRes) => {
    this.studentsRating = ratings
  }

  setCategoryId = (id: RatingCategoryId | undefined) => {
    this.categoryId = id
  }

  setCourses = (courses: IRatingStatisticCourse[]) => {
    this.courses = courses
  }

  setSortBy = (sortBy: IRatingLevelSort) => {
    this.sortBy = sortBy
  }

  setPage = (page: number) => {
    this.page = page
  }

  setPerPage = (perPage: number) => {
    this.perPage = perPage
  }

  setOrderBy = (orderBy: RatingOrderBy) => {
    this.orderBy = orderBy
  }

  setIsOpenGroupModal = (isOpen: boolean) => {
    this.isOpenGroupModal = isOpen
  }

  setSelectStudentGroup = (group: IRatingStatisticGroups[]) => {
    this.selectStudentGroup = group
  }

  setFilterevels = (levels: IRatingLevelByCategory[]) => {
    this.filterLevels = levels
  }

  setLevel = (level: number | null) => {
    this.level = level
  }

  setGroupId = (groupId: number | null) => {
    this.groupId = groupId
  }

  setCourseId = (courseId: number | null) => {
    this.courseId = courseId
  }

  setFrom = (from: string | null | Date) => {
    this.startDate = from
  }

  setTo = (to: string | null | Date) => {
    this.endDate = to
  }

  reset = () => {
    this.page = 1
    this.perPage = 10
  }
}

export const ratingStore = new RatingStore()
